export default defineAppConfig({
  ui: {
    colors: {
      primary: 'primary',
      secondary: 'secondary',
      neutral: 'neutral',
      success: 'success',
      info: 'secondary',
      warning: 'warning',
      error: 'error',
      dark: 'midnight',
    },

    accordion: {
      slots: {
        header:
          'px-4 data-[state=open]:bg-primary-50 dark:data-[state=open]:bg-primary-900/20',
        trigger: 'subtitle-2',
      },
    },

    badge: {
      compoundVariants: [
        {
          size: ['xs', 'sm', 'md', 'lg', 'xl'],
          class: 'rounded-full',
        },
      ],
      defaultVariants: {
        color: 'neutral',
        variant: 'subtle',
      },
    },

    button: {
      slots: {
        base: 'flex disabled:cursor-not-allowed text-default',
        leadingIcon: 'shrink-0',
      },
      variants: {
        size: {
          sm: {
            base: '!text-button-sm px-2.5 py-1.5',
            leadingIcon: 'h-3 w-3',
            trailingIcon: 'h-3 w-3',
          },
          md: {
            base: '!text-button px-3 py-2',
            leadingIcon: 'h-3.5 w-3.5',
            trailingIcon: 'h-3.5 w-3.5',
          },
          lg: {
            base: '!text-lg px-4 py-3',
            leadingIcon: 'h-5 w-5',
            trailingIcon: 'h-5 w-5',
          },
        },
      },
      defaultVariants: {
        color: 'neutral',
        size: 'sm',
        variant: 'solid',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: 'solid',
          class: 'bg-primary-600 hover:bg-primary-800 text-white',
        },
        {
          color: 'primary',
          variant: 'outline',
          class:
            'text-primary-600 ring-primary-600 hover:bg-primary-50 hover:ring-primary-800 hover:text-primary-800',
        },
        {
          color: 'success',
          variant: 'solid',
          class: 'bg-success-600 hover:bg-success-800',
        },
        {
          color: 'success',
          variant: 'outline',
          class:
            'text-success-600 ring-success-600 hover:bg-success-50 hover:ring-success-800 hover:text-success-800',
        },
        {
          color: 'warning',
          variant: 'solid',
          class: 'bg-warning-500 hover:bg-warning-700',
        },
        {
          color: 'warning',
          variant: 'outline',
          class:
            'text-warning-500 ring-warning-500 hover:bg-warning-50 hover:ring-warning-700 hover:text-warning-700',
        },
        {
          color: 'error',
          variant: 'solid',
          class: 'bg-error-600 hover:bg-error-800',
        },
        {
          color: 'error',
          variant: 'outline',
          class:
            'text-error-600 ring-error-600 hover:bg-error-50 hover:ring-error-800 hover:text-error-800',
        },
        {
          color: 'info',
          variant: 'solid',
          class: 'bg-info-600 hover:bg-info-800',
        },
        {
          color: 'info',
          variant: 'outline',
          class:
            'text-info-600 ring-info-600 hover:bg-info-50 hover:ring-info-800 hover:text-info-800',
        },
        {
          color: 'neutral',
          variant: 'solid',
          class: 'bg-neutral-600 hover:bg-neutral-800',
        },
        {
          color: 'neutral',
          variant: 'outline',
          class: 'text-neutral-900 ring-neutral-200 hover:bg-neutral-50',
        },
        {
          variant: 'link',
          color: 'secondary',
          class: 'text-link',
        },
        {
          variant: 'link',
          class: 'text-link-sm',
        },
      ],
    },

    checkbox: {
      slots: {
        base: 'dark:bg-accented dark:ring-default inset-shadow-xs',
      },
    },
    header: {
      slots: {
        root: 'bg-muted/75',
        title: 'text-base lg:text-lg font-semibold',
      },
    },
    input: {
      slots: {
        base: 'dark:bg-accented dark:ring-default inset-shadow-xs placeholder:text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 focus-visible:ring-secondary-800',
        },
      ],
    },
    inputDate: {
      slots: {
        segment: 'data-placeholder:text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 has-focus-visible:ring-secondary-800',
        },
      ],
    },
    inputMenu: {
      slots: {
        base: 'w-full dark:bg-accented dark:ring-default inset-shadow-xs placeholder:text-neutral-400',
        tagsInput: 'w-full placeholder:text-neutral-400',
        content: 'dark:bg-elevated',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 focus-visible:ring-secondary-800',
        },
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          multiple: true,
          class: 'outline-secondary-800 has-focus-visible:ring-secondary-800',
        },
        {
          multiple: false,
          class: { base: 'placeholder:text-neutral-400' },
        },
      ],
    },
    inputNumber: {
      slots: {
        base: 'placeholder:text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 focus-visible:ring-secondary-800',
        },
      ],
    },
    inputTags: {
      slots: {
        input: 'placeholder:text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 has-focus-visible:ring-secondary-800',
        },
      ],
    },
    inputTime: {
      slots: {
        segment: 'data-placeholder:text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 has-focus-visible:ring-secondary-800',
        },
      ],
    },
    pinInput: {
      slots: {
        base: 'placeholder:text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 focus-visible:ring-secondary-800',
        },
      ],
    },

    modal: {
      slots: {
        overlay: 'bg-neutral-900/75!',
      },
    },

    prose: {
      h3: {
        slots: {
          base: 'text-xs leading-4.5 font-medium tracking-[0.1px]',
        },
      },
      p: {
        base: 'leading-5 my-0',
      },
      ul: {
        base: 'my-0',
      },
      li: {
        base: 'my-0',
      },
    },

    radioGroup: {
      slots: {
        base: 'dark:bg-accented dark:ring-default inset-shadow-xs',
      },
    },

    select: {
      slots: {
        base: 'border border-default',
        placeholder: 'text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 focus-visible:ring-secondary-800',
        },
      ],
    },

    selectMenu: {
      slots: {
        base: 'w-full dark:bg-accented dark:ring-default inset-shadow-xs',
        placeholder: 'text-neutral-400',
        tagsInput: 'w-full',
        content: 'dark:bg-elevated',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 focus-visible:ring-secondary-800',
        },
      ],
    },

    switch: {
      slots: {
        base: 'data-[state=unchecked]:bg-accented inset-shadow-xs',
        thumb: 'border border-default',
      },
    },

    table: {
      slots: {
        separator: 'bg-(--ui-border)',
      },
    },

    tabs: {
      compoundVariants: [
        {
          orientation: 'horizontal',
          variant: 'link',
          class: {
            list: 'border-b -mb-px',
            indicator: 'bottom-0 h-[2px]',
          },
        },
      ],
    },

    textarea: {
      slots: {
        base: 'dark:bg-accented dark:ring-default placeholder:text-neutral-400',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'outline-secondary-800 focus-visible:ring-secondary-800',
        },
      ],
    },

    tooltip: {
      slots: {
        content:
          'h-auto rounded-md bg-neutral-700 px-4! py-3! text-sm text-white shadow-md ring-0',
        arrow: 'fill-neutral-700 stroke-neutral-700',
      },
    },
  },
})
